import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Bot, Search, Megaphone, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Layout } from "@/components/layout/Layout";
import { AnimatedSection } from "@/components/ui/AnimatedSection";
import { AnimatedCounter } from "@/components/ui/AnimatedCounter";
import { ServiceCard } from "@/components/ui/ServiceCard";
import { TestimonialCard } from "@/components/ui/TestimonialCard";
import { LogoSlider } from "@/components/ui/LogoSlider";
import { FloatingElement } from "@/components/ui/ParallaxSection";
import { ScrollProgress } from "@/components/ui/ScrollProgress";
import { TextReveal, TextRevealByWord } from "@/components/ui/TextReveal";

import { FloatingShapes } from "@/components/ui/GlowingOrb";
import { AnimatedBackground } from "@/components/ui/AnimatedBackground";
import { SEO } from "@/components/SEO";
import { useRef } from "react";
import { Link } from "react-router-dom";

const services = [
  {
    title: "GEO",
    description: "Get discovered by ChatGPT, Claude, and Perplexity. The new search frontier.",
    icon: Search,
    href: "/services/geo",
  },
  {
    title: "AI-Powered Ads",
    description: "Smarter campaigns on Google, Meta, and LinkedIn with AI optimization.",
    icon: Megaphone,
    href: "/services/ai-ads",
  },
  {
    title: "AI Content & SEO",
    description: "Scale content production 10x while maintaining quality and rankings.",
    icon: FileText,
    href: "/services/ai-content",
  },
  {
    title: "AI Agents",
    description: "Custom chatbots, lead qualification, and workflow automation.",
    icon: Bot,
    href: "/services/ai-agents",
  },
];

const stats = [
  { value: 312, suffix: "%", label: "Average ROI" },
  { value: 3, suffix: "x", label: "Pipeline Growth" },
  { value: 40, suffix: "%", label: "Cost Reduction" },
  { value: 50, suffix: "+", label: "B2B Clients" },
];

const testimonials = [
  {
    quote: "The Cloud SEO transformed our lead generation. We're now getting 3x more qualified leads at half the cost.",
    author: "Sarah Chen",
    role: "CEO",
    company: "TechScale SaaS",
  },
  {
    quote: "Their AI-powered approach to paid ads is unlike anything we've seen. Game-changing results.",
    author: "Michael Roberts",
    role: "Marketing Director",
    company: "DevForce Agency",
  },
  {
    quote: "Finally, a marketing agency that understands AI. They helped us get featured in ChatGPT responses.",
    author: "Priya Sharma",
    role: "Founder",
    company: "AI Solutions Co",
  },
];

export default function Index() {
  const heroRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });

  const heroY = useTransform(scrollYProgress, [0, 1], [0, 150]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.2]);
  const bgY = useTransform(scrollYProgress, [0, 1], [0, 100]);

  return (
    <Layout>
      <SEO 
        title="AI-Powered B2B Marketing Agency"
        description="Transform your B2B marketing with AI. GEO, AI-powered ads, content automation, and custom AI agents. 312% average ROI for clients."
        canonical="https://thecloudseo.com"
      />
      <ScrollProgress />
      
      {/* Hero Section with Parallax */}
      <section ref={heroRef} className="relative min-h-screen flex items-center justify-center pt-20 pb-16 overflow-hidden">
        {/* Animated Background */}
        <AnimatedBackground variant="mesh" />
        
        {/* Hero Background Image - Optimized loading */}
        <div className="absolute inset-0 z-0">
          <picture>
            <source
              type="image/avif"
              srcSet={
                "/images/hero-640.avif 640w, /images/hero-1024.avif 1024w, /images/hero-1600.avif 1600w, /images/hero-1920.avif 1920w"
              }
              sizes="100vw"
            />
            <source
              type="image/webp"
              srcSet={
                "/images/hero-640.webp 640w, /images/hero-1024.webp 1024w, /images/hero-1600.webp 1600w, /images/hero-1920.webp 1920w"
              }
              sizes="100vw"
            />
            <img
              src="/images/hero-1920.svg"
              srcSet={
                "/images/hero-640.svg 640w, /images/hero-1024.svg 1024w, /images/hero-1600.svg 1600w, /images/hero-1920.svg 1920w"
              }
              sizes="100vw"
              alt=""
              className="absolute inset-0 w-full h-full object-cover opacity-[0.03] dark:opacity-[0.08]"
              loading="eager"
              decoding="async"
              fetchPriority="high"
              width={1920}
              height={1080}
            />
          </picture>
          <div className="absolute inset-0 bg-gradient-to-b from-background via-background/95 to-background" />
        </div>

        {/* Parallax Background Elements */}
        <motion.div
          className="absolute inset-0 pointer-events-none z-0"
          style={{ y: bgY, scale: bgScale }}
        >
          <FloatingElement className="absolute top-1/4 left-[10%]" delay={0} amplitude={30}>
            <div className="w-32 h-32 rounded-full bg-primary/10 blur-3xl animate-morph" />
          </FloatingElement>
          <FloatingElement className="absolute top-1/3 right-[15%]" delay={1} amplitude={25}>
            <div className="w-40 h-40 rounded-full bg-primary/5 blur-3xl animate-glow-pulse" />
          </FloatingElement>
          <FloatingElement className="absolute bottom-1/4 left-[20%]" delay={2} amplitude={20}>
            <div className="w-24 h-24 rounded-full bg-primary/10 blur-3xl" />
          </FloatingElement>
          <FloatingElement className="absolute bottom-1/3 right-[10%]" delay={0.5} amplitude={35}>
            <div className="w-36 h-36 rounded-full bg-primary/5 blur-3xl animate-morph" />
          </FloatingElement>
        </motion.div>

        <motion.div 
          className="container-wide relative z-10"
          style={{ y: heroY, opacity: heroOpacity }}
        >
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, filter: "blur(10px)" }}
              animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary text-sm font-medium mb-8 hover-glow"
            >
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              AI-First Marketing Agency
            </motion.div>

            <TextReveal delay={0.1}>
              <h1 className="heading-hero mb-6">
                AI-Powered Growth
                <br />
                <span className="text-muted-foreground">for B2B Leaders</span>
              </h1>
            </TextReveal>

            <TextReveal delay={0.2}>
              <p className="text-xl md:text-2xl text-muted-foreground max-w-2xl mx-auto mb-10">
                Transform your marketing with intelligent automation. Get discovered by AI, 
                scale content, and convert leads on autopilot.
              </p>
            </TextReveal>

            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="flex flex-col sm:flex-row items-center justify-center gap-4"
            >
              <Link to="/contact">
                <Button
                  size="lg"
                  className="rounded-full px-8 text-base h-14 bg-primary text-primary-foreground hover:bg-primary/90 btn-magnetic"
                >
                  Book a Strategy Call
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/pricing">
                <Button
                  variant="outline"
                  size="lg"
                  className="rounded-full px-8 text-base h-14 hover-lift"
                >
                  View Pricing
                </Button>
              </Link>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* Logo Slider */}
      <section className="py-8 border-y border-border/50">
        <div className="container-wide">
          <p className="text-center text-sm text-muted-foreground mb-6">
            Trusted by innovative B2B companies
          </p>
          <LogoSlider />
        </div>
      </section>

      {/* Problem/Solution Section */}
      <section className="section-spacing bg-secondary/30 relative overflow-hidden">
        <FloatingShapes />
        <div className="container-narrow relative z-10">
          <AnimatedSection className="text-center max-w-3xl mx-auto">
            <TextRevealByWord 
              text="Traditional Marketing is Broken"
              className="heading-section mb-6 justify-center"
            />
            <p className="text-xl text-muted-foreground mb-12">
              Your competitors are using AI. Search is evolving. Content is everywhere.
              The old playbook doesn't work anymore.
            </p>
          </AnimatedSection>

          <AnimatedSection delay={0.2} className="text-center">
            <motion.div 
              className="inline-block p-6 rounded-2xl bg-background border border-border hover-glow"
              whileHover={{ scale: 1.02 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <p className="text-2xl md:text-3xl font-semibold text-shimmer">
                AI Changes Everything.
              </p>
            </motion.div>
          </AnimatedSection>
        </div>
      </section>

      {/* Services Section */}
      <section className="section-spacing relative overflow-hidden">
        <AnimatedBackground variant="dots" />
        
        <div className="container-wide relative z-10">
          <AnimatedSection className="text-center max-w-3xl mx-auto mb-16">
            <TextReveal>
              <h2 className="heading-section mb-6">What We Do</h2>
            </TextReveal>
            <p className="text-xl text-muted-foreground">
              Four pillars of AI-powered marketing to accelerate your growth.
            </p>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 gap-6">
            {services.map((service, index) => (
              <ServiceCard key={service.title} {...service} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="section-spacing bg-primary text-primary-foreground relative overflow-hidden">
        <motion.div 
          className="absolute inset-0 opacity-10"
          animate={{ 
            backgroundPosition: ["0% 0%", "100% 100%"],
          }}
          transition={{ 
            duration: 20, 
            repeat: Infinity, 
            repeatType: "reverse" 
          }}
          style={{
            backgroundImage: "radial-gradient(circle, white 1px, transparent 1px)",
            backgroundSize: "30px 30px",
          }}
        />
        <div className="container-wide relative z-10">
          <AnimatedSection className="text-center mb-16">
            <h2 className="heading-section mb-4">Real Results</h2>
            <p className="text-xl text-primary-foreground/70">
              Numbers that speak for themselves.
            </p>
          </AnimatedSection>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
            {stats.map((stat, index) => (
              <AnimatedSection key={stat.label} delay={index * 0.1} className="text-center">
                <motion.div 
                  className="text-5xl md:text-6xl lg:text-7xl font-bold mb-2"
                  initial={{ scale: 0.5, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ 
                    type: "spring", 
                    stiffness: 100, 
                    delay: index * 0.1 
                  }}
                >
                  <AnimatedCounter end={stat.value} suffix={stat.suffix} />
                </motion.div>
                <div className="text-primary-foreground/70">{stat.label}</div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="section-spacing relative overflow-hidden">
        <AnimatedBackground variant="gradient" />
        
        <div className="container-wide relative z-10">
          <AnimatedSection className="text-center max-w-3xl mx-auto mb-16">
            <TextReveal>
              <h2 className="heading-section mb-6">What Clients Say</h2>
            </TextReveal>
            <p className="text-xl text-muted-foreground">
              Hear from B2B leaders who transformed their growth with AI marketing.
            </p>
          </AnimatedSection>

          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <TestimonialCard key={index} {...testimonial} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-spacing bg-secondary/30 relative overflow-hidden">
        <FloatingShapes />
        <div className="container-narrow text-center relative z-10">
          <AnimatedSection>
            <TextReveal>
              <h2 className="heading-section mb-6">
                Ready to Transform
                <br />
                Your Growth?
              </h2>
            </TextReveal>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-10">
              Book a free strategy call to discover how AI-powered marketing 
              can accelerate your pipeline.
            </p>
            <Link to="/contact">
              <Button size="lg" className="rounded-full px-10 text-base h-14 btn-magnetic">
                Book a Strategy Call
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </AnimatedSection>
        </div>
      </section>
    </Layout>
  );
}
